import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Grid,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  logsService,
  AppointmentLog,
  LogFilters,
} from '../services/logsService';

const Logs: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Filters
  const [filters, setFilters] = useState<LogFilters>({
    page: 1,
    limit: 20,
  });
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [appointmentType, setAppointmentType] = useState<'all' | 'chat' | 'audio_call' | 'video_call'>('all');
  
  // Data
  const [appointments, setAppointments] = useState<AppointmentLog[]>([]);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 20,
    totalPages: 1,
  });
  
  const loadData = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const filtersWithDates = {
        ...filters,
        startDate: startDate ? startDate.toISOString().split('T')[0] : undefined,
        endDate: endDate ? endDate.toISOString().split('T')[0] : undefined,
        status: statusFilter || undefined,
        serviceType: appointmentType !== 'all' ? appointmentType : undefined,
      };

      const response = await logsService.getAppointmentLogs(filtersWithDates);
      if (response.success) {
        setAppointments(response.data.appointments || []);
        setPagination(prev => response.data.pagination || prev);
      } else {
        setError(response.message || 'Failed to load appointments');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load logs');
    } finally {
      setLoading(false);
    }
  }, [filters, startDate, endDate, statusFilter, appointmentType]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setFilters({ ...filters, page: value });
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending':
      case 'scheduled':
        return 'warning';
      case 'confirmed':
      case 'ongoing':
        return 'info';
      case 'completed':
        return 'success';
      case 'cancelled':
      case 'ended':
      case 'canceled':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Appointments
      </Typography>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={startDate ? startDate.toISOString().split('T')[0] : ''}
                onChange={(e) => setStartDate(e.target.value ? new Date(e.target.value) : null)}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="End Date"
                type="date"
                value={endDate ? endDate.toISOString().split('T')[0] : ''}
                onChange={(e) => setEndDate(e.target.value ? new Date(e.target.value) : null)}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="confirmed">Confirmed</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                  <MenuItem value="scheduled">Scheduled</MenuItem>
                  <MenuItem value="ongoing">Ongoing</MenuItem>
                  <MenuItem value="ended">Ended</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Search"
                value={filters.search || ''}
                onChange={(e) => setFilters({ ...filters, search: e.target.value, page: 1 })}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    loadData();
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Appointment Type</InputLabel>
                <Select
                  value={appointmentType}
                  label="Appointment Type"
                  onChange={(e) => {
                    setAppointmentType(e.target.value as 'all' | 'chat' | 'audio_call' | 'video_call');
                    setFilters({ ...filters, page: 1 });
                  }}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="chat">Chat Appointments</MenuItem>
                  <MenuItem value="audio_call">Audio Appointments</MenuItem>
                  <MenuItem value="video_call">Video Appointments</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      ) : (
        <Card>
          <CardContent>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Time</TableCell>
                    <TableCell>Patient</TableCell>
                    <TableCell>Doctor</TableCell>
                    <TableCell>Service Type</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Created</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {appointments.map((appointment) => (
                    <TableRow key={appointment.id}>
                      <TableCell>{appointment.id}</TableCell>
                      <TableCell>
                        {new Date(appointment.appointmentDate).toLocaleDateString()}
                      </TableCell>
                      <TableCell>{appointment.appointmentTime}</TableCell>
                      <TableCell>
                        {appointment.patient?.name || appointment.patientName}
                        <br />
                        <Typography variant="caption" color="textSecondary">
                          {appointment.patient?.email}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {appointment.doctor?.name || 'N/A'}
                        <br />
                        <Typography variant="caption" color="textSecondary">
                          {appointment.doctor?.email}
                        </Typography>
                      </TableCell>
                      <TableCell>{appointment.serviceType.replace('_', ' ')}</TableCell>
                      <TableCell>
                        <Chip
                          label={appointment.status}
                          color={getStatusColor(appointment.status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>₹{appointment.totalAmount}</TableCell>
                      <TableCell>
                        {new Date(appointment.createdAt).toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Box display="flex" justifyContent="center" p={2}>
              <Pagination
                count={pagination.totalPages}
                page={pagination.page}
                onChange={handlePageChange}
              />
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default Logs;
